import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEllipsisV,
  faPlus,
  faStepForward,
  faForward,
  faCheckCircle,
  faTrash,
  faMusic,
  faSyncAlt,
} from '@fortawesome/free-solid-svg-icons';

function MenuItem({ icon, label, onClick, danger, disabled }) {
  return (
    <button
      type="button"
      className={`track-more-menu-item${danger ? ' danger' : ''}`}
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
    >
      <FontAwesomeIcon icon={icon} fixedWidth />
      <span>{label}</span>
    </button>
  );
}

export default function TrackMoreMenu({
  isOpen,
  onToggle,
  onClose,
  playlists = [],
  trackId,
  isSkipped = false,
  onAddToPlaylist,
  onPlayNext,
  onSkip,
  onKeep,
  onUpdateInfo,
  onLyrics,
  onDelete,
  updating = false,
}) {
  const menuRef = useRef(null);
  const btnRef = useRef(null);
  const [pos, setPos] = useState(null);

  const updatePosition = useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    setPos({
      top: rect.top - 4,
      right: window.innerWidth - rect.right,
    });
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setPos(null);
      return;
    }
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;
    const handleOutsideClick = (e) => {
      const inMenu = menuRef.current?.contains(e.target);
      const onBtn = btnRef.current?.contains(e.target);
      if (!inMenu && !onBtn) onClose?.();
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [isOpen, onClose]);

  const run = (fn, close = true) => (e) => {
    e.stopPropagation();
    fn?.();
    if (close) onClose?.();
  };

  const memberPlaylists = (playlists || []).filter((p) => p.trackIds?.includes?.(trackId));

  const dropdown = isOpen && pos && (
    <div
      ref={menuRef}
      className="track-more-menu-dropdown track-more-menu-dropdown--portal"
      role="menu"
      style={{ top: pos.top, right: pos.right }}
    >
      <MenuItem
        icon={faPlus}
        label="Add to Playlist"
        onClick={run(onAddToPlaylist, false)}
      />
      <MenuItem
        icon={faSyncAlt}
        label={updating ? 'Looking up…' : 'Update info'}
        disabled={updating}
        onClick={run(onUpdateInfo, false)}
      />
      {isSkipped ? (
        <MenuItem icon={faCheckCircle} label="Keep" onClick={run(onKeep)} />
      ) : (
        <MenuItem icon={faStepForward} label="Play Next" onClick={run(onPlayNext)} />
      )}
      {!isSkipped && (
        <MenuItem icon={faForward} label="Skip" onClick={run(onSkip)} />
      )}
      <MenuItem icon={faMusic} label="Lyrics" onClick={run(onLyrics, false)} />

      <div className="track-more-menu-divider" />

      <MenuItem icon={faTrash} label="Delete" danger onClick={run(onDelete)} />

      {memberPlaylists.length > 0 && (
        <>
          <div className="track-more-menu-divider" />
          <div className="track-more-menu-playlist-bubbles">
            {memberPlaylists.map((p) => (
              <span key={p.name} className="track-more-menu-playlist-bubble">
                {p.name}
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="track-more-menu">
      <button
        ref={btnRef}
        type="button"
        className="track-more-btn"
        aria-label="Track options"
        aria-expanded={isOpen}
        aria-haspopup="menu"
        onClick={(e) => {
          e.stopPropagation();
          onToggle?.();
        }}
      >
        <FontAwesomeIcon icon={faEllipsisV} />
      </button>
      {dropdown && createPortal(dropdown, document.body)}
    </div>
  );
}
